/**
 * The API answers 503 "authorisation-pending" while a resource that was just created is not yet
 * known to the authentication backend. Such a request is repeated once access is set up.
 */
export const authorisationPendingRetryWindowMs = 60000;
const defaultRetryDelayMs = 5000;

type ApiError = {
  response?: {
    status?: number;
    headers?: Record<string, unknown>;
    data?: { errors?: unknown };
  };
};

export function isAuthorisationPending(error: ApiError): boolean {
  const errors = error?.response?.data?.errors;

  return (
    error?.response?.status === 503 &&
    errors !== null &&
    typeof errors === "object" &&
    Object.values(errors as Record<string, unknown>).some(
      (e) => (e as { title?: unknown } | null)?.title === "authorisation-pending",
    )
  );
}

export function authorisationPendingDelayMs(error: ApiError): number {
  const seconds = Number(error?.response?.headers?.["retry-after"]);

  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : defaultRetryDelayMs;
}

/** The delay before repeating the request, or null when it must not be repeated. */
export function nextAuthorisationPendingRetry(error: ApiError, firstFailureAt: number, now: number): number | null {
  if (!isAuthorisationPending(error)) {
    return null;
  }

  const delay = authorisationPendingDelayMs(error);

  return now + delay - firstFailureAt <= authorisationPendingRetryWindowMs ? delay : null;
}
